import Vue from 'vue'
import io from 'socket.io-client'
import { debounce } from 'lodash'

const logEvent = (message, data) => {
    if (window.location.href.includes('debug')) {
        console.log(message, data)
    }
}
const BATCH_SIZE = 10
const DEBOUNCE_WAIT = 200
const MAX_WAIT = 2000

export default {
    data() {
        return {
            unsubscribeFromBaseUpdates: null,
            unsubscribeFromSharingUpdates: null,
            baseQueue: Object.create(null),
            sharingQueue: Object.create(null),
            flushQueuesDebounced: debounce(this.flushQueues, DEBOUNCE_WAIT, {
                maxWait: MAX_WAIT,
            }),
        }
    },
    mounted() {
        Vue.prototype.$socket = io(process.env.VUE_APP_WEBSOCKET_URL, {
            transports: ['websocket'],
        })

        if (this.$store.state.auth.jwt?.access) {
            this.subscribeToBaseUpdates(this.$store.state.auth.jwt?.access)
        }

        if (this.$route.params.token) {
            this.subscribeToSharingUpdates(this.$route.params.token)
        }

        this.$watch(
            () => [
                this.$store.getters['map/liveUpdatesEnabledConditionally'],
                this.$store.state.auth.jwt?.access,
                this.$route.params.token,
            ],
            this.handleSubscriptions,
            { deep: true }
        )
    },
    beforeDestroy() {
        this.$socket.disconnect()
    },
    methods: {
        handleSubscriptions(
            [isLiveUpdatesEnabled, newBaseToken, newSharingToken],
            [wasLiveUpdatesEnabled, oldBaseToken, oldSharingToken]
        ) {
            if (!isLiveUpdatesEnabled) {
                this.unsubscribeFromBaseUpdates?.()
                this.unsubscribeFromSharingUpdates?.()
                return
            }

            if (!newBaseToken) {
                this.unsubscribeFromBaseUpdates?.()
            } else if (
                newBaseToken !== oldBaseToken ||
                !wasLiveUpdatesEnabled
            ) {
                this.unsubscribeFromBaseUpdates?.()
                this.subscribeToBaseUpdates(newBaseToken)
            }

            if (!newSharingToken) {
                this.unsubscribeFromSharingUpdates?.()
            } else if (
                newSharingToken !== oldSharingToken ||
                !wasLiveUpdatesEnabled
            ) {
                this.unsubscribeFromSharingUpdates?.()
                this.subscribeToSharingUpdates(newSharingToken)
            }
        },
        subscribeToBaseUpdates(token) {
            this.$socket.emit('authenticate', { token })

            const subscriptions = {
                asset_update: data => this.enqueue('base', data),
                debug: data => {
                    logEvent('Socket debug:', data)
                    if (data === 'Invalid token') {
                        this.$store.dispatch('auth/refreshTokenIfNecessary')
                    }
                },
            }

            Object.keys(subscriptions).forEach(key => {
                this.$socket.on(key, subscriptions[key])
            })

            this.unsubscribeFromBaseUpdates = () => {
                this.unsubscribeFromBaseUpdates = null
                Object.keys(subscriptions).forEach(key => {
                    this.$socket?.off(key, subscriptions[key])
                })
            }
        },
        subscribeToSharingUpdates(token) {
            this.$socket.emit('authenticate', { token })

            const subscriptions = {
                asset_update: data => this.enqueue('sharing', data),
            }

            Object.keys(subscriptions).forEach(key => {
                this.$socket.on(key, subscriptions[key])
            })

            this.unsubscribeFromSharingUpdates = () => {
                this.unsubscribeFromSharingUpdates = null
                Object.keys(subscriptions).forEach(key => {
                    this.$socket?.off(key, subscriptions[key])
                })
            }
        },
        enqueue(type, data) {
            logEvent('Received asset update', data)
            const queue = type === 'base' ? this.baseQueue : this.sharingQueue
            queue[data.tracker] = data
            if (
                this.$store.state.tracker.shouldUpdateTrackers &&
                Object.values(queue).length > BATCH_SIZE
            ) {
                this.flushQueuesDebounced.cancel()
                this.flushQueues()
                return
            }
            this.flushQueuesDebounced()
        },
        flushQueues() {
            if (!this.$store.state.tracker.shouldUpdateTrackers) {
                return
            }
            const baseItems = Object.values(this.baseQueue)
            const sharingItems = Object.values(this.sharingQueue)
            this.baseQueue = Object.create(null)
            this.sharingQueue = Object.create(null)
            if (baseItems.length) {
                logEvent('Updating items', baseItems)
                this.$store.dispatch(
                    'tracker/batchUpdateTrackerLive',
                    baseItems
                )
            }
            if (sharingItems.length) {
                logEvent('Updating items', sharingItems)
                this.$store.commit('sharing/batchUpdateTracker', sharingItems)
            }
        },
    },
}
